import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import MapRoutePicker from "../components/MapRoutePicker";
import PlacePhoto from "../components/PlacePhoto";
import { POPULAR_PLACES } from "../data/popularPlaces";

const ALL_PLACES = Object.entries(POPULAR_PLACES).flatMap(([regionSlug, places]) =>
  places.map((place) => ({ ...place, regionSlug }))
);

const normalizeSearchText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase()
  .trim();

export default function PlacesPage() {
  const { t } = useTranslation();
  const [regionFilter, setRegionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const regionSlugs = Object.keys(POPULAR_PLACES);
  const normalizedSearch = normalizeSearchText(search);

  const visiblePlaces = useMemo(() => ALL_PLACES.filter((place) => {
    const matchesRegion = regionFilter === "all" || place.regionSlug === regionFilter;
    const regionName = t(`regions.${place.regionSlug}.name`, place.regionSlug);
    const searchText = normalizeSearchText(`${place.name} ${place.address} ${regionName} ${place.regionSlug} ${place.id}`);
    const matchesSearch = !normalizedSearch
      || searchText.includes(normalizedSearch);
    return matchesRegion && matchesSearch;
  }), [regionFilter, normalizedSearch, t]);

  return (
    <main className="min-h-screen bg-[#F7F6F3] pb-20 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
        <header className="mb-7 sm:mb-9">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400">
            {t("places_catalog.badge")}
          </p>
          <h1 className="font-serif text-3xl font-bold sm:text-4xl">
            {t("places_catalog.title")}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-400 sm:text-base">
            {t("places_catalog.description")}
          </p>
        </header>

        <div className="mb-6 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("places_catalog.search")}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-400 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
            />
          </label>
          <div className="relative w-full sm:w-64">
            <select
              aria-label={t("places_catalog.region_filter")}
              value={regionFilter}
              onChange={(event) => setRegionFilter(event.target.value)}
              className="h-12 w-full appearance-none rounded-2xl border border-slate-200 bg-white pl-4 pr-12 text-sm font-semibold text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="all">{t("places_catalog.all_regions")}</option>
              {regionSlugs.map((slug) => (
                <option key={slug} value={slug}>{t(`regions.${slug}.name`, slug)}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden="true" />
          </div>
        </div>

        <p className="mb-4 text-xs font-semibold text-slate-500 dark:text-slate-400">
          {t("places_catalog.results", { count: visiblePlaces.length })}
        </p>

        {visiblePlaces.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visiblePlaces.map((place) => (
              <article
                key={place.id}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
              >
                <PlacePhoto
                  place={place}
                  regionSlug={place.regionSlug}
                  regionLabel={t(`regions.${place.regionSlug}.name`, place.regionSlug)}
                />
                <div className="flex flex-1 flex-col p-4 sm:p-5">
                  <h2 className="line-clamp-2 min-h-11 text-base font-bold leading-snug text-slate-900 dark:text-white sm:text-lg">{place.name}</h2>
                  <p className="mt-2 line-clamp-2 min-h-10 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                    {place.address || t("region_explorer.address_missing")}
                  </p>
                  <div className="mt-auto border-t border-slate-200 pt-3 dark:border-slate-800">
                    <MapRoutePicker place={place} label={t("region_explorer.build_route")} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-amber-300/70 bg-white/60 px-5 py-10 text-center text-sm text-slate-500 dark:border-amber-400/40 dark:bg-slate-900/60">
            {t("places_catalog.empty")}
          </div>
        )}
      </div>
    </main>
  );
}
